import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area,
  BarChart, Bar,
  RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import api from '../api';

/* ── Module registry ── */
const ALL_MODULES = [
  { key: 'users',       label: 'Users',        icon: '👥', path: '/admin/dashboard/users',      permission: 'users',          color: '#6366f1', glow: 'rgba(99,102,241,0.2)' },
  { key: 'devices',     label: 'Devices',      icon: '🖥',  path: '/admin/dashboard/devices',    permission: 'devices',        color: '#06b6d4', glow: 'rgba(6,182,212,0.2)'  },
  { key: 'appVersions', label: 'App Versions', icon: '📦', path: '/admin/dashboard/versions',   permission: 'appVersions',    color: '#10b981', glow: 'rgba(16,185,129,0.2)' },
  { key: 'plans',       label: 'Plans',        icon: '📋', path: '/admin/dashboard/plans',      permission: 'superAdminOnly', color: '#a855f7', glow: 'rgba(168,85,247,0.2)' },
  { key: 'payments',    label: 'Payments',     icon: '💳', path: '/admin/dashboard/payments',   permission: 'superAdminOnly', color: '#f59e0b', glow: 'rgba(245,158,11,0.2)'  },
  { key: 'roles',       label: 'Roles',        icon: '🛡',  path: '/admin/dashboard/roles',      permission: 'superAdminOnly', color: '#ef4444', glow: 'rgba(239,68,68,0.2)'  },
  { key: 'techUsers',   label: 'Tech Users',   icon: '👤', path: '/admin/dashboard/tech-users', permission: 'superAdminOnly', color: '#ec4899', glow: 'rgba(236,72,153,0.2)' },
];

const STAT_MAP = [
  { key: 'totalUsers',    label: 'Total Users',    icon: '👥', color: '#6366f1', bg: 'rgba(99,102,241,0.12)',  permission: 'users'          },
  { key: 'totalDevices',  label: 'Total Devices',  icon: '🖥',  color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   permission: 'devices'        },
  { key: 'totalPayments', label: 'Total Payments', icon: '💳', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  permission: 'superAdminOnly' },
  { key: 'totalRevenue',  label: 'Total Revenue',  icon: '💰', color: '#10b981', bg: 'rgba(16,185,129,0.12)',  permission: 'superAdminOnly', prefix: '$' },
  { key: 'activePlans',   label: 'Active Plans',   icon: '📋', color: '#a855f7', bg: 'rgba(168,85,247,0.12)',  permission: 'superAdminOnly' },
  { key: 'latestVersion', label: 'Latest Version', icon: '📦', color: '#ec4899', bg: 'rgba(236,72,153,0.12)',  permission: 'appVersions'    },
];

const ACTIVITY_FEED = [
  { icon: '🛡', text: 'Firewall rules synchronized',      time: '2 min ago',  color: '#6366f1' },
  { icon: '🔍', text: 'Threat scan completed',             time: '18 min ago', color: '#10b981' },
  { icon: '⚠️', text: 'Suspicious login attempt blocked', time: '45 min ago', color: '#f59e0b' },
  { icon: '🔒', text: 'Encryption keys rotated',           time: '2 hr ago',   color: '#06b6d4' },
  { icon: '✅', text: 'Security policy updated',           time: '5 hr ago',   color: '#a855f7' },
];

/* Custom tooltip shared by charts */
const ChartTooltip = ({ active, payload, label, prefix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e2340', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>{p.name}: {prefix}{p.value}</div>
      ))}
    </div>
  );
};

export default function DashboardHome() {
  const navigate     = useNavigate();
  const admin        = JSON.parse(localStorage.getItem('adminInfo') || '{}');
  const isSuperAdmin = admin.role === 'admin';
  const permissions  = admin.permissions || [];

  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  useEffect(() => {
    api.get('/admin/dashboard/stats')
      .then(({ data }) => { setStats(data.data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const canAccess = (p) => {
    if (p === null) return true;
    if (p === 'superAdminOnly') return isSuperAdmin;
    if (isSuperAdmin) return true;
    return permissions.includes(p);
  };

  const visibleStats   = STAT_MAP.filter(s => canAccess(s.permission));
  const visibleModules = ALL_MODULES.filter(m => canAccess(m.permission));
  const monthlyData    = stats?.monthlyData || [];

  /* Radial data — each module gets a fixed arc value for visual weight */
  const radialData = visibleModules.length > 0
    ? visibleModules.map((m, i) => ({ name: m.label, value: Math.max(20, 100 - i * 12), fill: m.color }))
    : [{ name: 'No modules', value: 100, fill: '#1e2340' }];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Welcome Banner ── */}
      <div style={s.banner}>
        <div style={s.bannerGlowLeft} />
        <div style={s.bannerGlowRight} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={s.bannerGreeting}>{greeting}, {admin.name || 'Admin'} 👋</div>
          <div style={s.bannerSub}>Monitor your security platform — live stats, trends & module access.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, position: 'relative', zIndex: 1 }}>
          <div style={s.bannerBadge}>
            <span style={s.pulseDot} />
            System Operational
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '14px 18px', color: '#f87171', fontSize: 13 }}>
          ⚠️ Could not load dashboard stats. Please check the server and refresh.
        </div>
      )}

      {/* ── Stat Cards ── */}
      {visibleStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(visibleStats.length, 4)}, 1fr)`, gap: 16 }}>
          {visibleStats.map(({ key, label, icon, color, bg, prefix }) => {
            const raw = stats?.[key];
            const val = loading ? '…' : (raw !== undefined && raw !== null ? raw : '—');
            return <StatCard key={key} value={val} label={label} icon={icon} color={color} bg={bg} prefix={prefix} />;
          })}
        </div>
      )}

      {/* ── Charts Row 1: Area + Bar ── */}
      <div style={s.chartsRow}>

        {/* Monthly Users — Area Chart */}
        <div style={{ ...s.card, flex: 3, minWidth: 0 }}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>📈 User Growth — Last 6 Months</span>
            <span style={s.chartBadge}>Monthly</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={monthlyData} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="users" name="Users" stroke="#6366f1" strokeWidth={2.5}
                fill="url(#gradUsers)" dot={{ fill: '#6366f1', r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: '#818cf8' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Payments — Bar Chart (super admin only) */}
        {isSuperAdmin && (
          <div style={{ ...s.card, flex: 2, minWidth: 0 }}>
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>💳 Payments — Last 6 Months</span>
              <span style={s.chartBadge}>Monthly</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData} margin={{ top: 8, right: 10, left: -20, bottom: 0 }} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="payments" name="Payments" fill="#f59e0b" radius={[4, 4, 0, 0]}
                  background={{ fill: 'rgba(255,255,255,0.02)', radius: [4, 4, 0, 0] }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── Charts Row 2: Revenue Area + Radial + Activity ── */}
      <div style={s.chartsRow}>

        {/* Revenue Trend — Area Chart (super admin only) */}
        {isSuperAdmin && (
          <div style={{ ...s.card, flex: 2, minWidth: 0 }}>
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>💰 Revenue Trend — Last 6 Months</span>
              <span style={s.chartBadge}>USD</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={monthlyData} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip prefix="$" />} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2.5}
                  fill="url(#gradRevenue)" dot={{ fill: '#10b981', r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: '#34d399' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Module Distribution — Radial Bar */}
        {visibleModules.length > 0 && (
          <div style={{ ...s.card, flex: 1.4, minWidth: 220 }}>
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>⊞ Module Overview</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{visibleModules.length} active</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="25%" outerRadius="90%"
                data={radialData} startAngle={180} endAngle={-180}>
                <RadialBar minAngle={15} dataKey="value" cornerRadius={4} background={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: '#1e2340', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                      <span style={{ color: d.fill, fontWeight: 600 }}>{d.name}</span>
                    </div>
                  );
                }} />
              </RadialBarChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 4 }}>
              {visibleModules.map(m => (
                <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Activity Feed */}
        <div style={{ ...s.card, flex: 1.4, minWidth: 220 }}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>🔔 Security Activity</span>
            <span style={s.liveBadge}><span style={s.pulseDot} />Live</span>
          </div>
          <div>
            {ACTIVITY_FEED.map((a, i) => (
              <div key={i} style={{ ...s.activityItem, borderBottom: i < ACTIVITY_FEED.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ ...s.activityIcon, background: a.color + '22', color: a.color }}>{a.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.activityText}>{a.text}</div>
                  <div style={s.activityTime}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Module Quick Access ── */}
      {visibleModules.length > 0 && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>⊞ Quick Access</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{visibleModules.length} modules available</span>
          </div>
          <div style={s.modulesGrid}>
            {visibleModules.map(m => (
              <ModuleCard key={m.key} module={m} onClick={() => navigate(m.path)} />
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

/* ── Stat Card ── */
function StatCard({ value, label, icon, color, bg, prefix }) {
  const showPrefix = prefix && value !== '…' && value !== '—';
  return (
    <div style={s.statCard}>
      <div style={{ ...s.statAccentBar, background: `linear-gradient(90deg, ${color}, transparent)` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ ...s.statValue, color }}>
            {showPrefix ? prefix : ''}{value}
          </div>
          <div style={s.statLabel}>{label}</div>
        </div>
        <div style={{ ...s.statIcon, background: bg, color }}>{icon}</div>
      </div>
    </div>
  );
}

/* ── Module Card ── */
function ModuleCard({ module: m, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        ...s.moduleCard,
        borderColor: hovered ? m.color + '66' : 'var(--border)',
        boxShadow:   hovered ? `0 4px 20px ${m.glow}` : 'none',
        transform:   hovered ? 'translateY(-3px)' : 'none',
        background:  hovered ? `linear-gradient(135deg, ${m.color}10, var(--bg-card-hover))` : 'var(--bg-card-hover)',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ ...s.moduleIcon, background: m.color + '20', color: m.color }}>{m.icon}</div>
      <div style={s.moduleLabel}>{m.label}</div>
      <div style={{ ...s.moduleArrow, color: hovered ? m.color : 'var(--text-muted)' }}>→</div>
    </div>
  );
}

/* ── Styles ── */
const s = {
  banner: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #1a1f3a 50%, #0f172a 100%)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 14, padding: '24px 28px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
    position: 'relative', overflow: 'hidden',
  },
  bannerGlowLeft: {
    position: 'absolute', top: -60, left: -40, width: 220, height: 220, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bannerGlowRight: {
    position: 'absolute', bottom: -60, right: 60, width: 180, height: 180, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bannerGreeting: { fontSize: 22, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 },
  bannerSub:      { fontSize: 13, color: '#94a3b8' },
  bannerBadge: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#34d399',
  },
  pulseDot: {
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
    background: '#10b981', boxShadow: '0 0 8px #10b981', flexShrink: 0,
  },

  statCard: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '18px 20px',
    position: 'relative', overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    transition: 'all 0.2s ease',
  },
  statAccentBar: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
  },
  statValue: { fontSize: 30, fontWeight: 800, lineHeight: 1, marginBottom: 6 },
  statLabel: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.7 },
  statIcon:  { width: 46, height: 46, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 },

  card: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '20px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)',
  },
  cardTitle:  { fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 7 },
  chartBadge: {
    fontSize: 10, fontWeight: 600, color: '#64748b',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20, padding: '3px 10px', textTransform: 'uppercase', letterSpacing: 0.5,
  },
  liveBadge: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#34d399', fontWeight: 600 },

  chartsRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },

  activityItem: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0' },
  activityIcon: { width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 },
  activityText: { fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.4 },
  activityTime: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },

  modulesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 },
  moduleCard: {
    border: '1px solid var(--border)', borderRadius: 10, padding: '16px 12px',
    cursor: 'pointer', transition: 'all 0.2s ease',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center',
  },
  moduleIcon:  { width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 },
  moduleLabel: { fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' },
  moduleArrow: { fontSize: 13, transition: 'color 0.2s' },
};
